/**
 * Pure logic behind the storage setup wizard.
 *
 * The wizard's job is to let someone describe a freezer the way they would say
 * it out loud — "ten racks, each holding ten boxes, each a nine by nine" — and
 * turn that into the sequence of API calls the storage model needs. All of the
 * arithmetic, validation and naming lives here rather than in the component,
 * because this repo's vitest runs `.ts` only: logic left in a `.tsx` would be
 * untestable.
 *
 * Two vocabularies meet in this file and must not be confused. `WizardKind` is
 * what the user sees (Section / Rack / Box / Slot); `StorageNodeKind` is what
 * goes on the wire. `toNodeKind` is the only crossing point.
 */

import type { SlotLabelScheme, StorageNodeKind } from '@/types/storage'
import { DEFAULT_LABEL_PREFIX, MAX_GRID_POSITIONS, gridCellLabels } from './slotLabels'

/** What the user picks from, in lab words. */
export type WizardKind = 'SECTION' | 'RACK' | 'BOX' | 'SLOT'

/**
 * How deep a wizard-built tree may go beneath its root.
 *
 * The server caps a tree at 12 levels overall. A unit sits at depth 0 and the
 * wizard only ever builds downwards from it, so four described levels is far
 * inside the limit while still covering section → rack → box → slot.
 */
export const MAX_LEVEL_DEPTH = 4

const NODE_KIND: Record<WizardKind, StorageNodeKind> = {
  // A "section" is a named compartment — the design note's `Vapor`. ZONE on
  // the wire; nobody in a lab calls it a zone.
  SECTION: 'ZONE',
  RACK: 'RACK',
  BOX: 'BOX',
  SLOT: 'POSITION',
}

const WORD: Record<WizardKind, { one: string; many: string }> = {
  SECTION: { one: 'section', many: 'sections' },
  RACK: { one: 'rack', many: 'racks' },
  BOX: { one: 'box', many: 'boxes' },
  SLOT: { one: 'slot', many: 'slots' },
}

/** Node codes may not contain the path separator, and must start alphanumeric. */
const CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 _.-]*$/

const ORDER: WizardKind[] = ['SECTION', 'RACK', 'BOX', 'SLOT']

export function toNodeKind(kind: WizardKind): StorageNodeKind {
  return NODE_KIND[kind]
}

/**
 * The other direction, for prefilling the wizard from an existing unit.
 *
 * Derived from `NODE_KIND` rather than written out again — two hand-kept
 * tables would eventually disagree, and this is the crossing between the two
 * vocabularies the file's header warns about.
 */
const WIZARD_KIND = Object.fromEntries(
  Object.entries(NODE_KIND).map(([wizard, wire]) => [wire, wizard as WizardKind]),
) as Record<string, WizardKind | undefined>

export function toWizardKind(kind: StorageNodeKind): WizardKind | null {
  return WIZARD_KIND[kind] ?? null
}

export function kindWord(kind: WizardKind, count: number): string {
  return count === 1 ? WORD[kind].one : WORD[kind].many
}

/**
 * What may sit inside what.
 *
 * Levels only ever go downwards, so each answer narrows the next question —
 * and a slot ends the chain, because a slot holds stock rather than more
 * structure.
 */
export function childKindOptions(parent: WizardKind | null): WizardKind[] {
  if (parent === null) return [...ORDER]
  return ORDER.slice(ORDER.indexOf(parent) + 1)
}

/**
 * One level as the server describes an existing unit — the wire vocabulary.
 * Mirrors `LayoutLevel` in @/types/storage; declared here so this module stays
 * dependency-free apart from its own types.
 */
export type TemplateLevel = {
  kind: StorageNodeKind
  codes?: string[]
  rows?: number
  columns?: number
  scheme?: SlotLabelScheme
  prefix?: string
}

/** A named level: sections are called things, not numbered. */
export type SectionLevel = { kind: 'SECTION'; names: string[] }

/** A counted level: racks, boxes and slots are enumerated by a grid. */
export type GridLevel = {
  kind: Exclude<WizardKind, 'SECTION'>
  rows: number
  columns: number
  scheme: SlotLabelScheme
  prefix?: string
}

export type WizardLevel = SectionLevel | GridLevel

export function defaultLevelFor(kind: WizardKind): WizardLevel {
  if (kind === 'SECTION') return { kind: 'SECTION', names: [] }
  const scheme: SlotLabelScheme = kind === 'SLOT' ? 'ROW_COLUMN' : 'SEQUENTIAL'
  const level: GridLevel = { kind, rows: 1, columns: 1, scheme }
  if (scheme === 'SEQUENTIAL') level.prefix = DEFAULT_LABEL_PREFIX[toNodeKind(kind)] ?? 'N'
  return level
}

/** Section names come from a textarea: one per line, or comma-separated. */
export function parseSectionNames(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

/**
 * The codes a level mints, in order — none while the level cannot be built.
 *
 * Called while rendering, on whatever the author has typed so far. A field
 * cleared to 0 (or past the grid cap) is a normal moment mid-edit, not an
 * error: `validateLevel` says what is wrong and disables the buttons, and this
 * returns nothing rather than letting `gridCellLabels` throw and take the whole
 * page down (z8nrz7f29c). `sampleAddress` already treated a level the same way.
 */
export function levelCodes(level: WizardLevel): string[] {
  if (level.kind === 'SECTION') return level.names
  try {
    return gridCellLabels({
      rows: level.rows,
      columns: level.columns,
      scheme: level.scheme,
      prefix: level.prefix,
    }).map((c) => c.code)
  } catch {
    return []
  }
}

/** The inline message for one Rows / Columns field, or null when it is fine. */
export function dimensionProblem(value: number): string | null {
  return Number.isInteger(value) && value >= 1 ? null : 'Must be at least 1.'
}

/** How many children this level creates under each parent. */
export function levelCount(level: WizardLevel): number {
  return level.kind === 'SECTION' ? level.names.length : level.rows * level.columns
}

/**
 * Why a level cannot be built yet, in a sentence a user can act on, or null.
 *
 * The grid cap is checked here rather than left to the server so the number
 * appears while they are still typing, next to the field that caused it.
 */
export function validateLevel(level: WizardLevel, depth: number): string | null {
  if (depth > MAX_LEVEL_DEPTH) {
    return `That would be ${depth} levels deep — storage can go ${MAX_LEVEL_DEPTH} levels below a unit.`
  }

  if (level.kind === 'SECTION') {
    if (level.names.length === 0) return 'Give at least one section a name.'
    const seen = new Set<string>()
    for (const name of level.names) {
      const key = name.toUpperCase()
      if (seen.has(key)) return `“${name}” is listed twice — each section needs its own name.`
      seen.add(key)
      if (name.includes('/')) return `“${name}” cannot contain “/”.`
      if (!CODE_PATTERN.test(name)) {
        return `“${name}” must start with a letter or number, and use only letters, numbers, spaces, . _ or -.`
      }
      if (name.length > 64) return `“${name}” is too long — 64 characters at most.`
    }
    return null
  }

  const { rows, columns } = level
  if (!Number.isInteger(rows) || !Number.isInteger(columns)) {
    return 'Use whole numbers for rows and columns.'
  }
  if (rows < 1 || columns < 1) return 'Rows and columns must each be at least 1.'
  const total = rows * columns
  if (total > MAX_GRID_POSITIONS) {
    return `${rows} × ${columns} is ${total} ${kindWord(level.kind, total)} in one go — the limit is ${MAX_GRID_POSITIONS}.`
  }
  if (level.prefix != null && level.prefix.length > 0 && !CODE_PATTERN.test(level.prefix)) {
    return `Prefix "${level.prefix}" must start with a letter or number, and use only letters, numbers, spaces, . _ or -.`
  }
  return null
}

export type LevelTotal = { kind: WizardKind; perParent: number; total: number }

/**
 * How many nodes each level adds in total.
 *
 * Each level applies to every node the level above created — that uniformity
 * is the whole reason the wizard is quicker than declaring grids by hand — so
 * the totals multiply down the chain.
 */
/**
 * How many nodes one `POST /storage-nodes/:id/layout` may create.
 *
 * Mirrors `MAX_LAYOUT_NODES` in cgt-backend's `storage-tree.ts`, per this
 * workspace's duplicated-by-design rule — **keep the two in step**. The server
 * can raise it with an env var and remains the authority; this copy exists so
 * an impossible plan is refused on the screen that composed it, naming the
 * number, rather than coming back as a 400 after the author has finished.
 */
export const MAX_LAYOUT_NODES = 100_000

/** Every node a plan would create, summed across its levels. */
export function planNodeTotal(levels: WizardLevel[]): number {
  return planTotals(levels).reduce((sum, level) => sum + level.total, 0)
}

/**
 * Every node a plan would WRITE A ROW for — which is not the same number.
 *
 * A slot is described by the grid on the box above it: dimensions, child kind
 * and label scheme name every cell, so the cells themselves are derived on read
 * and only get a row once something makes one different. Containers do get
 * rows; there are few of them, and they are what people navigate, name, block
 * and assign conditions to.
 *
 * The gap is the whole point. A hundred racks of a hundred boxes of a hundred
 * slots is 1,010,100 slots and 10,100 rows, and it is rows that bound how long
 * one request takes to write.
 *
 * Mirrors `shouldMintCells` in cgt-backend's `storage.service.ts`, per this
 * workspace's duplicated-by-design rule — **keep the two in step**.
 */
export function planStoredTotal(levels: WizardLevel[]): number {
  return planTotals(levels)
    .filter((level) => level.kind !== 'SLOT')
    .reduce((sum, level) => sum + level.total, 0)
}

/**
 * Why this whole plan cannot be built in one request, or null.
 *
 * Distinct from `validateLevel`, which judges one level in isolation: every
 * level here can be individually sane and still multiply into millions, since
 * each one applies to every node the level above produced. Six racks of ten
 * boxes of a hundred slots is three reasonable-looking numbers and 6,066 nodes;
 * change the first to six hundred and it is 606,600.
 */
export function validatePlan(levels: WizardLevel[]): string | null {
  if (levels.length === 0) return null

  // Measured in rows written, not slots described — a level of slots costs no
  // rows at all, so a million-slot freezer is 10,100 rows and well inside this.
  // Counting slots here refused layouts the server would have written in
  // seconds, and made the ceiling read as a limit on how big a freezer may be
  // rather than on how much one request may write.
  const stored = planStoredTotal(levels)
  if (stored <= MAX_LAYOUT_NODES) return null

  return (
    `This plan would create ${stored.toLocaleString()} containers — ` +
    `one request can create ${MAX_LAYOUT_NODES.toLocaleString()}. ` +
    `Build the outer levels on their own, then lay out each one separately — or describe fewer racks or boxes.`
  )
}

/** One line of the wizard's running picture of the unit. */
export type BlueprintRow = {
  kind: WizardKind
  /** How many this level puts inside EACH parent. */
  perParent: number
  /** How many exist across the whole unit at this level. */
  total: number
  /** What the first one will be called. */
  firstCode: string | null
  /** And the last — null when there is only one, so no range is printed. */
  lastCode: string | null
}

/**
 * The unit as described so far, level by level.
 *
 * The wizard shows this on every screen, because the thing being described is
 * a nested object and answering one question at a time hides its shape: after
 * three answers you could not see what you had said, only that you were three
 * screens in. Each row carries the two counts that matter — per parent, which
 * is what you answered, and the total, which is what it multiplies to — plus
 * the codes it will actually mint, so the naming is visible before anything
 * is written rather than after.
 */
export function planBlueprint(levels: WizardLevel[]): BlueprintRow[] {
  const totals = planTotals(levels)
  return levels.map((level, i) => {
    const codes = level.kind === 'SECTION' ? level.names : levelCodes(level)
    return {
      kind: level.kind,
      perParent: totals[i].perParent,
      total: totals[i].total,
      firstCode: codes[0] ?? null,
      lastCode: codes.length > 1 ? codes[codes.length - 1] : null,
    }
  })
}

/**
 * What a vial in this unit would be addressed as.
 *
 * The payoff of the whole exercise, shown while the plan is still being
 * composed. "Ten racks of ten boxes of a hundred slots" is arithmetic;
 * `LN2-01 / R05 / C05 / E5` is the thing an operator will read off a label and
 * walk to, and seeing it form is what makes the nesting concrete.
 *
 * Takes the cell at the MIDDLE of each grid rather than the first. Every first
 * code is R01 / C01 / A1, which reads as one repeated thing and hides that the
 * levels are independently labelled; the middle of the grid gives a genuine
 * address and happens to be the one a person would point at.
 */
export function sampleAddress(levels: WizardLevel[], rootCode: string): string {
  const segments = [rootCode]
  for (const level of levels) {
    const code = middleCode(level)
    // A level mid-edit can have no code at all — an empty section list, a
    // dimension typed down to nothing. It contributes no segment rather than
    // breaking the address.
    if (code) segments.push(code)
  }
  return segments.join(' / ')
}

/** The label of the cell at the centre of a level, or null while it has none. */
function middleCode(level: WizardLevel): string | null {
  if (level.kind === 'SECTION') {
    return level.names[Math.floor(level.names.length / 2)] ?? null
  }
  try {
    const cells = gridCellLabels({
      rows: level.rows,
      columns: level.columns,
      scheme: level.scheme,
      prefix: level.prefix,
    })
    const row = Math.ceil(level.rows / 2)
    const column = Math.ceil(level.columns / 2)
    const middle = cells.find((c) => c.gridRow === row && c.gridColumn === column)
    return middle?.code ?? null
  } catch {
    return null
  }
}

export function planTotals(levels: WizardLevel[]): LevelTotal[] {
  const out: LevelTotal[] = []
  let parents = 1
  for (const level of levels) {
    const perParent = levelCount(level)
    const total = parents * perParent
    out.push({ kind: level.kind, perParent, total })
    parents = total
  }
  return out
}

/**
 * A step the wizard will execute. `create` mints named nodes one at a time;
 * `declare` declares one grid on every parent the previous level produced.
 *
 * `parentCodes` carries `<root>` for the first level, meaning "the unit the
 * wizard was opened on" — the component resolves that to a real node id. Codes
 * rather than ids because this module never sees the server's rows.
 */
export type PlanStep =
  | { type: 'create'; levelIndex: number; codes: string[] }
  | {
      type: 'declare'
      levelIndex: number
      parentCodes: string[]
      rows: number
      columns: number
    }

export const ROOT_PARENT = '<root>'

/**
 * Expand the described levels into the calls that build them.
 *
 * The expansion is what makes "each rack holds ten boxes" one answer instead
 * of ten: level N is declared on every code level N−1 produced.
 */
export function buildPlanSteps(levels: WizardLevel[]): PlanStep[] {
  const steps: PlanStep[] = []
  let parentCodes: string[] = [ROOT_PARENT]

  for (const [levelIndex, level] of levels.entries()) {
    if (level.kind === 'SECTION') {
      steps.push({ type: 'create', levelIndex, codes: level.names })
      parentCodes = level.names
      continue
    }
    steps.push({
      type: 'declare',
      levelIndex,
      parentCodes,
      rows: level.rows,
      columns: level.columns,
    })
    parentCodes = levelCodes(level)
  }

  return steps
}

/**
 * An existing unit's described levels, turned into wizard levels to edit.
 *
 * The server speaks `StorageNodeKind` and the wizard speaks lab words, so this
 * is the crossing — and the defaults matter as much as the mapping. A level
 * the source left to a server default (`scheme`, `prefix`) arrives without
 * one; filling them in the way the server would is what stops a copy silently
 * relabelling every rack it reproduces.
 *
 * A wire kind the wizard has no word for is dropped rather than guessed at —
 * the person is editing the result and can see a level is missing, which is
 * better than a level that claims to be something it is not.
 */
export function levelsFromTemplate(levels: TemplateLevel[]): WizardLevel[] {
  const out: WizardLevel[] = []

  for (const level of levels) {
    const kind = toWizardKind(level.kind)
    if (!kind) continue

    if (level.codes && level.codes.length > 0) {
      // A named level is a section however the server kinded it: codes, not a
      // count, is what makes it one.
      out.push({ kind: 'SECTION', names: [...level.codes] })
      continue
    }

    if (level.rows == null || level.columns == null) continue
    if (kind === 'SECTION') continue

    const scheme: SlotLabelScheme = level.scheme ?? (kind === 'SLOT' ? 'ROW_COLUMN' : 'SEQUENTIAL')
    const grid: GridLevel = { kind, rows: level.rows, columns: level.columns, scheme }
    // ROW_COLUMN labels are A1…I9 and use no prefix; carrying one across would
    // put a value in a field the level does not read.
    if (scheme === 'SEQUENTIAL') {
      grid.prefix = level.prefix ?? DEFAULT_LABEL_PREFIX[toNodeKind(kind)] ?? 'N'
    }
    out.push(grid)
  }

  return out
}
